import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AdminOrganization } from '../models/admin.models';
import { AdminService } from './admin.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationContextService {
  private readonly storage = inject(StorageService);
  private readonly adminService = inject(AdminService);

  private readonly idSignal = signal<number | null>(this.storage.getSelectedOrganizationId());
  private readonly nameSignal = signal<string | null>(this.storage.getSelectedOrganizationName());
  private readonly organizationsSignal = signal<AdminOrganization[] | null>(null);
  private organizationsPromise: Promise<AdminOrganization[]> | null = null;

  readonly selectedOrganizationId = this.idSignal.asReadonly();
  readonly selectedOrganizationName = this.nameSignal.asReadonly();
  /** Whether there is more than one organization to switch between; controls the "Cambiar" header option. */
  readonly hasMultipleOrganizations = computed(() => (this.organizationsSignal()?.length ?? 0) > 1);

  select(organization: { id: number; name: string }): void {
    this.idSignal.set(organization.id);
    this.nameSignal.set(organization.name);
    this.storage.setSelectedOrganization(organization.id, organization.name);
  }

  clear(): void {
    this.idSignal.set(null);
    this.nameSignal.set(null);
    this.storage.clearSelectedOrganization();
    this.organizationsSignal.set(null);
    this.organizationsPromise = null;
  }

  /** Fetches the organizations available to the current super admin, caching the result. */
  loadOrganizations(): Promise<AdminOrganization[]> {
    const cached = this.organizationsSignal();
    if (cached) {
      return Promise.resolve(cached);
    }

    if (!this.organizationsPromise) {
      this.organizationsPromise = firstValueFrom(this.adminService.organizations({ per_page: 200 }))
        .then((response) => {
          this.organizationsSignal.set(response.data);
          return response.data;
        })
        .catch(() => {
          this.organizationsPromise = null;
          return [];
        });
    }

    return this.organizationsPromise;
  }
}
